import { useState } from 'react';
import type { ITag } from '../types/tag';
import { DeleteTagDialog } from './DeleteTagDialog';
import { DialogTrigger } from './ui/dialog';

export interface ITagListItemProps {
  tag: ITag;
  index: number;
  onRemove: (id: string) => void;
  onUpdate: (id: string, updates: Partial<ITag>) => void;
  isNewTag?: boolean;
}

export function TagListItem({ tag, index, onRemove, onUpdate, isNewTag = false }: ITagListItemProps) {
  const [isEditing, setIsEditing] = useState(isNewTag);
  const [editName, setEditName] = useState(tag.name);
  const [editDescription, setEditDescription] = useState(tag.description || '');
  const [showFullDescription, setShowFullDescription] = useState(false);

  const handleSave = () => {
    if (!editName.trim()) return;
    onUpdate(tag.id, {
      name: editName.trim(),
      description: editDescription.trim() || '',
    });
    setIsEditing(false);
  };

  const handleCancel = () => {
    if (isNewTag) {
      onRemove(tag.id);
    } else {
      setEditName(tag.name);
      setEditDescription(tag.description || '');
      setIsEditing(false);
    }
  };

  const truncateDescription = (text: string, maxLength: number = 60) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (isEditing) {
    return (
      <div className="bg-card border border-border rounded-lg p-3">
        <div className="flex flex-col gap-2 font-mono text-sm">
          <input
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            placeholder="Tag name"
            className="bg-background text-foreground border border-border rounded px-2 py-1 text-xs font-bold focus:outline-none focus:border-primary"
            autoFocus={isNewTag}
          />
          <textarea
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            placeholder="Description (optional)"
            rows={3}
            className="bg-background text-foreground border border-border rounded px-2 py-1 text-xs resize-none focus:outline-none focus:border-primary"
          />
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={!editName.trim()}
              className="bg-primary text-primary-foreground disabled:bg-muted disabled:text-muted-foreground border-none rounded px-3 py-1 text-xs font-bold cursor-pointer disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
            >
              Save
            </button>
            <button
              onClick={handleCancel}
              className="bg-transparent text-muted-foreground hover:text-foreground border border-border rounded px-3 py-1 text-xs font-bold cursor-pointer transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-3 flex justify-between items-center">
      <div className="flex-1 font-mono text-sm">
        <div className="font-bold text-foreground mb-1 text-xs">
          #{tag.name}
        </div>
        {tag.description && (
          <div className="text-xs text-muted-foreground leading-relaxed mb-2">
            {showFullDescription ? (
              <div>
                {tag.description}
                {tag.description.length > 60 && (
                  <button
                    onClick={() => setShowFullDescription(false)}
                    className="ml-2 text-primary hover:underline cursor-pointer bg-transparent border-none p-0"
                  >
                    [Show less]
                  </button>
                )}
              </div>
            ) : (
              <div>
                {truncateDescription(tag.description)}
                {tag.description.length > 60 && (
                  <button
                    onClick={() => setShowFullDescription(true)}
                    className="ml-2 text-primary hover:underline cursor-pointer bg-transparent border-none p-0"
                  >
                    [Show more]
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 ml-3">
        <button
          onClick={() => setIsEditing(true)}
          className="bg-transparent text-muted-foreground hover:text-foreground border-none rounded w-6 h-6 cursor-pointer text-sm flex items-center justify-center flex-shrink-0 transition-colors"
          title="Edit tag"
        >
          ✎
        </button>
        <DeleteTagDialog
          tagName={tag.name}
          onConfirm={() => onRemove(tag.id)}
          trigger={
            <DialogTrigger asChild>
              <button
                className="bg-transparent text-muted-foreground hover:text-foreground border-none rounded w-6 h-6 cursor-pointer text-lg flex items-center justify-center flex-shrink-0 transition-colors"
                title="Remove tag"
              >
                ×
              </button>
            </DialogTrigger>
          }
        />
      </div>
    </div>
  );
}
